import React, { useState, useEffect, useCallback } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Editor } from 'primereact/editor';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Dropdown } from 'primereact/dropdown';
import { announcementApi } from '../services/api';
import 'quill/dist/quill.snow.css';

const severityOptions = [
  { label: 'Information', value: 'info' },
  { label: 'Warning', value: 'warning' },
  { label: 'Important', value: 'error' },
];

const repeatOptions = [
  { label: 'One-time (never again after I understand, until you save a new notice)', value: 'once' },
  { label: 'Repeating — every N hours', value: 'interval' },
  { label: 'Repeating — once per day after a set time', value: 'daily' },
];

function isoToDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function datetimeLocalToIso(s) {
  if (!s || !String(s).trim()) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function hourMinuteToTime(h, m) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(Math.max(0, Math.min(23, h | 0)))}:${pad(Math.max(0, Math.min(59, m | 0)))}`;
}

function timeToHourMinute(t) {
  if (!t || typeof t !== 'string') return { h: 9, m: 0 };
  const [a, b] = t.split(':');
  let h = parseInt(a, 10);
  let m = parseInt(b || '0', 10);
  if (!Number.isFinite(h)) h = 9;
  if (!Number.isFinite(m)) m = 0;
  return { h: Math.max(0, Math.min(23, h)), m: Math.max(0, Math.min(59, m)) };
}

const UserAnnouncement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [severity, setSeverity] = useState('warning');
  const [repeatMode, setRepeatMode] = useState('once');
  const [repeatIntervalHours, setRepeatIntervalHours] = useState('24');
  const [dailyTime, setDailyTime] = useState('09:00');
  const [campaignStartLocal, setCampaignStartLocal] = useState('');
  const [campaignEndLocal, setCampaignEndLocal] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  const messagePlain = String(message || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const willShowBanner = messagePlain.length > 0;

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const data = await announcementApi.get();
      setTitle(data.title || '');
      setMessage(data.message || '');
      setSeverity(data.severity || 'warning');
      setRepeatMode(data.repeatMode === 'interval' || data.repeatMode === 'daily' ? data.repeatMode : 'once');
      setRepeatIntervalHours(String(data.repeatIntervalHours != null ? data.repeatIntervalHours : 24));
      setDailyTime(hourMinuteToTime(data.dailyAnchorHour ?? 9, data.dailyAnchorMinute ?? 0));
      setCampaignStartLocal(isoToDatetimeLocal(data.campaignStartsAt));
      setCampaignEndLocal(isoToDatetimeLocal(data.campaignEndsAt));
      setUpdatedAt(data.updatedAt || null);
      setUpdatedBy(data.updatedBy || null);
    } catch (err) {
      setError(err.message || 'Failed to load announcement');
    } finally {
      setLoading(false);
    }
  }, []);

  const MAX_MESSAGE_TEXT_LEN = 2000;

  const editorHeaderTemplate = (
    <span className="ql-formats">
      <select className="ql-header" defaultValue="">
        <option value="1">Heading</option>
        <option value="">Normal</option>
      </select>
      <button type="button" className="ql-bold" aria-label="Bold" />
      <button type="button" className="ql-italic" aria-label="Italic" />
      <button type="button" className="ql-underline" aria-label="Underline" />
      <button type="button" className="ql-list" value="ordered" aria-label="Ordered list" />
      <button type="button" className="ql-list" value="bullet" aria-label="Bullet list" />
      <select className="ql-align" defaultValue="" aria-label="Text alignment">
        <option value="">Left</option>
        <option value="center">Center</option>
        <option value="right">Right</option>
        <option value="justify">Justify</option>
      </select>
      <button type="button" className="ql-clean" aria-label="Clear formatting" />
    </span>
  );

  useEffect(() => {
    load();
  }, [load]);

  const buildSaveBody = (overrides = {}) => {
    const msg = String(overrides.message !== undefined ? overrides.message : message).trim();
    const { h, m } = timeToHourMinute(dailyTime);
    let hours = parseInt(repeatIntervalHours, 10);
    if (!Number.isFinite(hours) || hours < 1) hours = 24;
    hours = Math.min(168, hours);

    return {
      enabled: msg.length > 0,
      title: overrides.title !== undefined ? overrides.title : title,
      message: overrides.message !== undefined ? overrides.message : message,
      severity: overrides.severity !== undefined ? overrides.severity : severity,
      repeatMode,
      repeatIntervalHours: hours,
      dailyAnchorHour: h,
      dailyAnchorMinute: m,
      campaignStartsAt: datetimeLocalToIso(
        overrides.campaignStartLocal !== undefined ? overrides.campaignStartLocal : campaignStartLocal
      ),
      campaignEndsAt: datetimeLocalToIso(
        overrides.campaignEndLocal !== undefined ? overrides.campaignEndLocal : campaignEndLocal
      ),
    };
  };

  const persist = async (overrides = {}) => {
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const body = buildSaveBody(overrides);
      const data = await announcementApi.save(body);
      setTitle(data.title || '');
      setMessage(data.message || '');
      setSeverity(data.severity || 'warning');
      setRepeatMode(data.repeatMode === 'interval' || data.repeatMode === 'daily' ? data.repeatMode : 'once');
      setRepeatIntervalHours(String(data.repeatIntervalHours != null ? data.repeatIntervalHours : 24));
      setDailyTime(hourMinuteToTime(data.dailyAnchorHour ?? 9, data.dailyAnchorMinute ?? 0));
      setCampaignStartLocal(isoToDatetimeLocal(data.campaignStartsAt));
      setCampaignEndLocal(isoToDatetimeLocal(data.campaignEndsAt));
      setUpdatedAt(data.updatedAt || null);
      setUpdatedBy(data.updatedBy || null);
      return data;
    } catch (err) {
      setError(err.message || 'Save failed');
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      await persist({});
      setSuccess('Saved.');
      setTimeout(() => setSuccess(''), 5000);
    } catch {
      /* set in persist */
    }
  };

  const handleClearAndHide = async () => {
    try {
      await persist({ title: '', message: '' });
      setSuccess('Cleared and saved. App users will not see a banner until you add text again.');
      setTimeout(() => setSuccess(''), 6000);
    } catch {
      /* set in persist */
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem clamp(1.25rem, 4vw, 2.5rem)', maxWidth: 720, margin: '0 auto' }}>
        <p style={{ color: '#64748b', margin: 0 }}>Loading…</p>
      </div>
    );
  }

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 600,
    fontSize: '0.875rem',
    color: '#334155',
  };

  const fieldShell = { display: 'flex', flexDirection: 'column', gap: 0 };

  const cardHeader = (
    <div
      style={{
        padding: '1.125rem clamp(1.25rem, 4vw, 2rem)',
        borderBottom: '1px solid #e2e8f0',
        background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.75rem',
      }}
    >
      <span style={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a' }}>Announcement</span>
      <span
        style={{
          fontSize: '0.8rem',
          fontWeight: 600,
          padding: '0.35rem 0.75rem',
          borderRadius: 999,
          ...(willShowBanner
            ? { background: '#dcfce7', color: '#166534' }
            : { background: '#e2e8f0', color: '#475569' }),
        }}
      >
        {willShowBanner ? 'Will show in app (after Save)' : 'Hidden (empty message)'}
      </span>
    </div>
  );

  return (
    <div
      style={{
        padding: '2rem clamp(1.25rem, 4vw, 2.5rem) 3rem',
        maxWidth: 720,
        margin: '0 auto',
        boxSizing: 'border-box',
      }}
    >
      <header style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ margin: '0 0 0.5rem', fontSize: '1.625rem', fontWeight: 800, color: '#0f172a' }}>
          App announcement
        </h1>
        <p style={{ margin: 0, color: '#64748b', lineHeight: 1.6, fontSize: '0.95rem' }}>
          Short banner after sign-in. <strong>I understand</strong> is logged. Message empty = off. Set how often
          it comes back and optional active dates below.
        </p>
      </header>

      {error && (
        <Message severity="error" text={error} style={{ width: '100%', marginBottom: '1.25rem' }} />
      )}
      {success && (
        <Message severity="success" text={success} style={{ width: '100%', marginBottom: '1.25rem' }} />
      )}

      <Card
        header={cardHeader}
        className="user-announcement-card"
        style={{
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
          border: '1px solid #e2e8f0',
        }}
      >
        <style>
          {`
            .user-announcement-card .p-card-body { padding: 0 !important; }
            .announcement-actions .p-button {
              padding: 0.65rem 1.2rem !important;
              min-height: 2.75rem;
              display: inline-flex !important;
              align-items: center !important;
              gap: 0.45rem !important;
              font-weight: 600 !important;
            }
            .announcement-actions .p-button .p-button-icon { margin: 0 !important; }
          `}
        </style>
        <div
          style={{
            padding: 'clamp(1.25rem, 3vw, 1.65rem) clamp(1.25rem, 4vw, 2rem) 1.75rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
          }}
        >
          <div style={fieldShell}>
            <label htmlFor="ann-title" style={labelStyle}>
              Title <span style={{ fontWeight: 500, color: '#94a3b8' }}>(optional)</span>
            </label>
            <InputText
              id="ann-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', padding: '0.65rem 0.85rem' }}
              maxLength={120}
            />
          </div>

          <div style={fieldShell}>
            <label htmlFor="ann-msg" style={labelStyle}>
              Message
            </label>
            <Editor
              value={message}
              onTextChange={(e) => {
                const plain = String(e.textValue || '').trim();
                if (plain.length <= MAX_MESSAGE_TEXT_LEN) {
                  setMessage(String(e.htmlValue || ''));
                }
              }}
              style={{ minHeight: '180px' }}
              placeholder="Text shown in the app. Leave empty to turn off."
              headerTemplate={editorHeaderTemplate}
            />
            <div style={{ textAlign: 'right', marginTop: '0.35rem', fontSize: '0.8rem', color: '#94a3b8' }}>
              {messagePlain.length} / {MAX_MESSAGE_TEXT_LEN}
            </div>
          </div>

          <div style={fieldShell}>
            <label htmlFor="ann-sev" style={labelStyle}>
              Style
            </label>
            <Dropdown
              inputId="ann-sev"
              value={severity}
              options={severityOptions}
              onChange={(e) => setSeverity(e.value)}
              style={{ width: '100%', maxWidth: 360 }}
            />
          </div>

          <div
            style={{
              padding: '1rem 0 0',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <span style={{ ...labelStyle, marginBottom: 0 }}>After user taps &quot;I understand&quot;</span>
            <Dropdown value={repeatMode} options={repeatOptions} onChange={(e) => setRepeatMode(e.value)} />

            {repeatMode === 'interval' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <label htmlFor="ann-hours" style={{ ...labelStyle, marginBottom: 0 }}>
                  Hours until it can show again
                </label>
                <InputText
                  id="ann-hours"
                  type="number"
                  min={1}
                  max={168}
                  value={repeatIntervalHours}
                  onChange={(e) => setRepeatIntervalHours(e.target.value)}
                  style={{ width: 100, padding: '0.5rem' }}
                />
                <span style={{ fontSize: '0.85rem', color: '#64748b' }}>1–168 (e.g. 24 = once per day)</span>
              </div>
            )}

            {repeatMode === 'daily' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                <label htmlFor="ann-daily" style={{ ...labelStyle, marginBottom: 0 }}>
                  New &quot;day&quot; starts at (local time on device)
                </label>
                <input
                  id="ann-daily"
                  type="time"
                  value={dailyTime}
                  onChange={(e) => setDailyTime(e.target.value)}
                  style={{ padding: '0.5rem 0.65rem', fontSize: '1rem', borderRadius: 6, border: '1px solid #cbd5e1' }}
                />
              </div>
            )}

            <div style={fieldShell}>
              <span style={labelStyle}>
                {repeatMode === 'once' ? 'Start (optional)' : 'Active only between (optional)'}
              </span>
              <p style={{ margin: '0 0 0.65rem', fontSize: '0.82rem', color: '#64748b' }}>
                {repeatMode === 'once'
                  ? 'Optional: set a start time so the one-time banner does not appear before that moment.'
                  : 'Outside this window the app never shows the banner. Example: end = last day 23:59 so after the campaign ends, even daily repeats stop.'}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                  <label htmlFor="c-start" style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Start
                  </label>
                  <input
                    id="c-start"
                    type="datetime-local"
                    value={campaignStartLocal}
                    onChange={(e) => setCampaignStartLocal(e.target.value)}
                    style={{
                      display: 'block',
                      marginTop: 4,
                      padding: '0.5rem',
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                    }}
                  />
                </div>
                {repeatMode !== 'once' && (
                  <div>
                    <label htmlFor="c-end" style={{ fontSize: '0.75rem', color: '#64748b' }}>
                      End
                    </label>
                    <input
                      id="c-end"
                      type="datetime-local"
                      value={campaignEndLocal}
                      onChange={(e) => setCampaignEndLocal(e.target.value)}
                      style={{
                        display: 'block',
                        marginTop: 4,
                        padding: '0.5rem',
                        borderRadius: 6,
                        border: '1px solid #cbd5e1',
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className="announcement-actions"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.75rem',
              paddingTop: '1.25rem',
              borderTop: '1px solid #e2e8f0',
            }}
          >
            <Button label={saving ? 'Saving…' : 'Save'} icon="pi pi-check" onClick={handleSave} disabled={saving} />
            <Button
              label="Reload"
              icon="pi pi-refresh"
              className="p-button-outlined"
              onClick={load}
              disabled={saving}
            />
            <Button
              label="Clear"
              icon="pi pi-trash"
              className="p-button-outlined p-button-secondary"
              onClick={handleClearAndHide}
              disabled={saving || !willShowBanner}
            />
          </div>

          <p style={{ margin: 0, fontSize: '0.82rem', color: '#94a3b8', lineHeight: 1.5 }}>
            <strong>Save</strong> — publish. <strong>Reload</strong> — discard unsaved edits. <strong>Clear</strong>{' '}
            — empty message and save.
          </p>

          {(updatedAt || updatedBy) && (
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#94a3b8' }}>
              Last saved
              {updatedAt ? ` · ${new Date(updatedAt).toLocaleString()}` : ''}
              {updatedBy ? ` · ${updatedBy}` : ''}
            </p>
          )}
        </div>
      </Card>
    </div>
  );
};

export default UserAnnouncement;
